import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Save, Copy, Code, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({Authorization: `Bearer ${localStorage.getItem('recupnet_token')}`});
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AtendimentoWebchatConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/webchat/config`).then(r => setCfg(r.data)).catch(() => setCfg({
      enabled:true,agentName:'RecupBot',subtitle:'Atendimento inteligente RecupNet',
      welcomeMessage:'Ola! Sou o RecupBot da RecupNet.',primaryColor:'#63CBB6',position:'right',
      askNameBeforeChat:false,askPhoneBeforeChat:false,askEmailBeforeChat:false,
      offlineMessage:'Nosso time esta offline.',businessHours:{start:'08:00',end:'18:00'},
    })).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/atendimento/webchat/config`, cfg, {headers: headers()});
      toast.success('Configuracao salva');
    } catch { toast.error('Erro'); }
    finally { setSaving(false); }
  };

  const snippet = `<!-- RecupNet Webchat Widget -->\n<script src="${BACKEND}/webchat-widget.js"></script>\n<script>RecupNetChat.init({ apiUrl: '${BACKEND}' });</script>`;

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:28,height:28}}/></div>;

  return (
    <div className="p-6 space-y-5 overflow-y-auto" style={{maxHeight:'100vh'}}>
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-semibold">Webchat</h1><p className="text-sm text-white/40">Widget de atendimento para sites</p></div>
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2 text-sm">
          {saving?<span className="rn-spinner" style={{width:14,height:14}}/>:<><Save size={16}/> Salvar</>}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Config */}
        <div className="space-y-4">
          <div className="glass-card space-y-3">
            <h3 className="text-sm font-semibold text-white/60">Aparencia</h3>
            <F label="Nome do assistente" value={cfg?.agentName} onChange={v => setCfg({...cfg,agentName:v})}/>
            <F label="Subtitulo" value={cfg?.subtitle} onChange={v => setCfg({...cfg,subtitle:v})}/>
            <F label="Cor principal" value={cfg?.primaryColor} onChange={v => setCfg({...cfg,primaryColor:v})} type="color"/>
            <div><label className="text-xs text-white/40 block mb-1">Mensagem de boas-vindas</label>
              <textarea value={cfg?.welcomeMessage||''} onChange={e => setCfg({...cfg,welcomeMessage:e.target.value})} rows={3}
                className="w-full bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
            </div>
          </div>

          <div className="glass-card space-y-3">
            <h3 className="text-sm font-semibold text-white/60">Coleta de dados</h3>
            <Tog label="Pedir nome antes do chat" value={cfg?.askNameBeforeChat} onChange={v => setCfg({...cfg,askNameBeforeChat:v})}/>
            <Tog label="Pedir telefone" value={cfg?.askPhoneBeforeChat} onChange={v => setCfg({...cfg,askPhoneBeforeChat:v})}/>
            <Tog label="Pedir email" value={cfg?.askEmailBeforeChat} onChange={v => setCfg({...cfg,askEmailBeforeChat:v})}/>
          </div>

          <div className="glass-card space-y-3">
            <h3 className="text-sm font-semibold text-white/60">Horario</h3>
            <div className="grid grid-cols-2 gap-3">
              <F label="Inicio" value={cfg?.businessHours?.start} onChange={v => setCfg({...cfg,businessHours:{...(cfg?.businessHours||{}),start:v}})} type="time"/>
              <F label="Fim" value={cfg?.businessHours?.end} onChange={v => setCfg({...cfg,businessHours:{...(cfg?.businessHours||{}),end:v}})} type="time"/>
            </div>
            <div><label className="text-xs text-white/40 block mb-1">Mensagem offline</label>
              <textarea value={cfg?.offlineMessage||''} onChange={e => setCfg({...cfg,offlineMessage:e.target.value})} rows={2}
                className="w-full bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
            </div>
          </div>
        </div>

        {/* Snippet + Preview */}
        <div className="space-y-4">
          <div className="glass-card">
            <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2"><Code size={16}/> Snippet para incorporacao</h3>
            <pre className="p-3 rounded-xl bg-black/30 text-xs font-mono overflow-x-auto" style={{color:'var(--rn-green)'}}>{snippet}</pre>
            <button onClick={() => {navigator.clipboard.writeText(snippet); toast.success('Copiado!');}} className="btn-secondary text-xs mt-3 flex items-center gap-1"><Copy size={12}/> Copiar snippet</button>
          </div>

          <div className="glass-card">
            <h3 className="text-sm font-semibold text-white/60 mb-3">Endpoints do Webchat</h3>
            <div className="space-y-1 text-xs text-white/40">
              <p><code className="text-[var(--rn-green)]">POST /api/webchat/session</code> — Criar sessao</p>
              <p><code className="text-[var(--rn-green)]">GET /api/webchat/config</code> — Config publica</p>
              <p><code className="text-[var(--rn-green)]">POST /api/webchat/messages</code> — Enviar mensagem</p>
              <p><code className="text-[var(--rn-green)]">GET /api/webchat/messages/:id</code> — Polling</p>
            </div>
          </div>

          <div className="glass-card text-center py-6">
            <Globe size={32} className="mx-auto text-white/15 mb-2"/>
            <p className="text-xs text-white/35">O widget aparece no canto inferior direito do seu site apos incorporar o snippet.</p>
            <p className="text-[10px] text-white/20 mt-1">Para testar, acesse /webchat-demo</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({label,value,onChange,type='text'}) {
  return (<div><label className="text-xs text-white/40 block mb-1">{label}</label><input type={type} value={value||''} onChange={e=>onChange(e.target.value)}
    className={`${type==='color'?'w-12 h-9 p-0 border-0':'w-full'} bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]`}/></div>);
}
function Tog({label,value,onChange}) {
  return (<label className="flex items-center gap-3 cursor-pointer"><div onClick={()=>onChange(!value)} className={`w-9 h-5 rounded-full transition-colors ${value?'bg-[var(--rn-green)]':'bg-white/10'}`}><div className={`w-4 h-4 rounded-full bg-white mt-0.5 transition-transform ${value?'translate-x-4.5 ml-[18px]':'ml-0.5'}`}/></div><span className="text-xs text-white/60">{label}</span></label>);
}
